document.addEventListener("DOMContentLoaded", function() {
  if (!customElements.get('pk-expandable-content')) {
    class PkExpandableContent extends HTMLElement {
      constructor() {
        super();
    
        this.content = null;
        this.toggleButton = null;
        this.isExpanded = false;
    
        this.toggleContent = this.toggleContent.bind(this);
      }
  
      connectedCallback() {
        try {
          this.init();
        } catch (error) {
          console.error('PkExpandableContent initialization error:', error.message);
        }
      }
  
      disconnectedCallback() {
        this.toggleButton.removeEventListener('click', this.toggleContent);
      }
      
      init() {
        this.content = this.querySelector('[data-content]');
        this.toggleButton = this.querySelector('[data-button]');
    
        if (!this.content || !this.toggleButton) {
          throw new Error('Required data-content or data-button elements are missing in the DOM.');
        }
    
        if (!this.validateMaxHeightVariable()) {
          throw new Error('Missing or invalid --pk-expandable-content-max-height CSS variable. Set a non-negative value.');
        }
    
        if (!this.shouldDisplayToggleButton()) {
          this.toggleButton.remove();
          return;
        }
    
        this.toggleButton.classList.add('pk-expandable-content__toggle-button--visible');
        
        this.initButtonLabels();
        this.setInitialState();
        this.attachEventListeners();
      }
    
      initButtonLabels() {
        const labels = this.toggleButton.getAttribute('data-button');
        if (!labels) {
          throw new Error('data-button attribute is missing or empty.');
        }
    
        const [showMoreLabel, showLessLabel] = labels.split(',');
        if (!showMoreLabel || !showLessLabel) {
          throw new Error('data-button attribute must contain two labels separated by a comma, e.g., "Show more,Show less".');
        }
    
        this.showMoreLabel = showMoreLabel.trim();
        this.showLessLabel = showLessLabel.trim();
      }
    
      setInitialState() {
        const maxHeight = this.getCSSVariable('--pk-expandable-content-max-height');
    
        this.content.style.maxHeight = maxHeight;
        this.content.setAttribute('aria-expanded', 'false');
        this.toggleButton.textContent = this.showMoreLabel;
      }
    
      attachEventListeners() {
        this.toggleButton.addEventListener('click', this.toggleContent);
      }
    
      toggleContent() {
        this.isExpanded = !this.isExpanded;
        const maxHeight = this.getCSSVariable('--pk-expandable-content-max-height');
    
        this.content.style.maxHeight = this.isExpanded ? 'none' : maxHeight;
        this.content.setAttribute('aria-expanded', this.isExpanded);
        this.toggleButton.textContent = this.isExpanded
          ? this.showLessLabel
          : this.showMoreLabel;
      }
    
      /**
       * Validate the `--pk-expandable-content-max-height` CSS variable to ensure it is valid.
       * @returns {boolean} True if the variable is valid, false otherwise.
       */
      validateMaxHeightVariable() {
        const maxHeight = this.getCSSVariable('--pk-expandable-content-max-height');
        const numericHeight = parseInt(maxHeight, 10);
        
        return !Number.isNaN(numericHeight) && numericHeight >= 0;
      }
    
      /**
       * Determines whether the toggle button should be displayed based on the content's height.
       * @returns {boolean} True if the button should be displayed, false otherwise.
       */
      shouldDisplayToggleButton() {
        const maxHeight = parseInt(this.getCSSVariable('--pk-expandable-content-max-height'), 10);
        const contentHeight = this.content.scrollHeight;
        
        return contentHeight > maxHeight;
      }
    
      /**
       * @param {string} variableName - The name of the CSS variable to retrieve.
       * @returns {string|null} The value of the CSS variable, or null if not set.
       */
      getCSSVariable(variableName) {
        return getComputedStyle(this.content).getPropertyValue(variableName).trim();
      }
    }
    
    customElements.define('pk-expandable-content', PkExpandableContent);
  };
  
  if (!customElements.get('pk-modal')) {
    class PKModal extends HTMLElement {
      constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.init();
      }
    
      init() {
        this.render();
      }
    
      connectedCallback() {
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
        }
    
        this.modal = this.shadowRoot.querySelector(".modal");
        this.backdrop = this.shadowRoot.querySelector(".backdrop");
        this.trigger = document.querySelector(`[aria-controls="${this.id}"]`);
        this.closeSlot = this.shadowRoot.querySelector("slot[name='close']");
    
        this.handleEscape = this.handleEscape.bind(this);
        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleBackdropClick = this.handleBackdropClick.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleSlotChange = this.handleSlotChange.bind(this);
    
        if (this.trigger) {
          this.trigger.addEventListener("click", this.handleTriggerClick);
        } else {
          console.warn(
            `PKModal: No trigger found for modal with id "${this.id}". ` +
            `Ensure an element has [aria-controls="${this.id}"].`
          );
        }
    
        this.initEvents();
      }
    
      disconnectedCallback() {
        document.removeEventListener("keydown", this.handleEscape);
        
        this.trigger?.removeEventListener("click", this.handleTriggerClick);
        this.backdrop?.removeEventListener("click", this.handleBackdropClick);
        this.closeSlot?.removeEventListener("slotchange", this.handleSlotChange);
        
        if (this.closeBtn) {
          this.closeBtn.removeEventListener("click", this.handleCloseClick);
          this.closeBtn = null;
        }
      }
    
      toggleModal(open) {
        const isOpen = this.hasAttribute("open");
        if (open === isOpen) return;
    
        this.toggleAttribute("open", open);
        this.trigger?.setAttribute("aria-expanded", open ? "true" : "false");
        document.body.style.overflow = open ? "hidden" : "";
    
        if (open) {
          this.modal.setAttribute("tabindex", "-1");
          this.modal.focus();
          document.addEventListener("keydown", this.handleEscape);
          this.dispatchEvent(new CustomEvent("open", {
            detail: { id: this.id, content: this.innerHTML }
          })); 
        } else {
          this.trigger?.focus();
          document.removeEventListener("keydown", this.handleEscape);
          this.dispatchEvent(new CustomEvent("close", {
            detail: { id: this.id }
          }));
        }
      }
    
      initEvents() {
        this.backdrop?.addEventListener("click", this.handleBackdropClick);
        this.closeSlot?.addEventListener("slotchange", this.handleSlotChange);
      }
    
      handleSlotChange() {
        const newCloseBtn = this.querySelector('[slot="close"]');
    
        if (this.closeBtn && this.closeBtn !== newCloseBtn) {
          this.closeBtn.removeEventListener("click", this.handleCloseClick);
        }
    
        this.closeBtn = newCloseBtn;
    
        if (this.closeBtn) {
          this.closeBtn.addEventListener("click", this.handleCloseClick);
        }
      }
    
      handleTriggerClick() {
        this.toggleModal(true);
      }
    
      handleBackdropClick() {
        this.toggleModal(false);
      }
    
      handleCloseClick() {
        this.toggleModal(false);
      }
    
      handleEscape(event) {
        if (event.key === "Escape") {
          this.toggleModal(false);
        }
      }
    
      render() {
        this.shadowRoot.innerHTML = `
          <style>
            :host {
              display: none;
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background: var(--pk-modal-overlay-color, rgba(0, 0, 0, 0.6));
              justify-content: center;
              align-items: center;
              z-index: 2147483647;
            }
    
            .modal {
              padding: var(--pk-modal-padding, 40px);
              position: relative;
              display: flex;
              flex-direction: column;
              width: var(--pk-modal-width, 100%);
              min-width: var(--pk-modal-min-width, 80%);
              max-width: var(--pk-modal-max-width, 80%);
              height: var(--pk-modal-height, 100%);
              min-height: var(--pk-modal-min-height, 70%);
              max-height: var(--pk-modal-max-height, 70%);
              color: var(--pk-modal-color, initial);
              background: var(--pk-modal-background, white);
              border-radius: var(--pk-modal-border-radius, 0);
              opacity: 0;
              transform: scale(0.9);
              transition: transform 0.2s ease-out, opacity 0.2s ease-out;
              overflow: overlay;
              scrollbar-width: thin;
              z-index: 2147483647;
            }
    
            :host([open]) {
              display: flex;
            }
    
            :host([open]) .modal {
              transform: scale(1);
              opacity: 1;
            }
    
            .backdrop {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background: transparent;
            }
    
            ::slotted([slot="close"]) {
              position: absolute;
              top: 8px;
              right: 8px;
              background: none;
              border: none;
              cursor: pointer;
              font-size: var(--pk-close-btn-size, 16px);
            }
          </style>
    
          <div class="backdrop"></div>
          <div class="modal">
            <slot name="close"></slot>
            <slot></slot>
          </div>
        `;
      }
    }
    
    customElements.define("pk-modal", PKModal);
  };
  
  if (!customElements.get('pk-tabs')) {
    class PKTabs extends HTMLElement {
      constructor() {
        super();
        this.tabs = null;
        this.contents = null;
      }
    
      connectedCallback() {
        this.init();
        this.initEvents();
      }
    
      disconnectedCallback() {
        this.cleanupEvents();
      }
    
      init() {
        this.tabs = this.querySelectorAll('[role="tab"]');
        this.contents = this.querySelectorAll('[role="tabpanel"]');
    
        if (this.tabs.length > 0 && this.contents.length > 0) {
          this.contents.forEach((content, index) => {
            content.style.display = index === 0 ? "block" : "none";
          });
    
          this.tabs.forEach((tab, index) => {
            tab.setAttribute("aria-selected", index === 0 ? "true" : "false");
            tab.setAttribute("tabindex", index === 0 ? "0" : "-1");
          });
        }
      }
    
      initEvents() {
        this.tabs.forEach((tab, index) => {
          tab.addEventListener("click", this.selectTab.bind(this, index));
          tab.addEventListener("keydown", this.handleKeydown.bind(this, index));
        });
      }
    
      cleanupEvents() {
        this.tabs.forEach((tab, index) => {
          tab.removeEventListener("click", this.selectTab.bind(this, index));
          tab.removeEventListener("keydown", this.handleKeydown.bind(this, index));
        });
      }
    
      selectTab(index) {
        this.tabs.forEach((tab, i) => {
          const isSelected = i === index;
          tab.setAttribute("aria-selected", isSelected ? "true" : "false");
          tab.setAttribute("tabindex", isSelected ? "0" : "-1");
        });
    
        this.contents.forEach((content, i) => {
          content.style.display = i === index ? "block" : "none";
        });
    
        this.tabs[index].focus();
      }
    
      handleKeydown(index, event) {
        if (event.key === "ArrowRight") {
          this.selectTab((index + 1) % this.tabs.length);
        } else if (event.key === "ArrowLeft") {
          this.selectTab((index - 1 + this.tabs.length) % this.tabs.length);
        }
      }
    }
    
    customElements.define("pk-tabs", PKTabs);
  };
  
  if (!customElements.get('pk-external-video')) {
    class PkExternalVideo extends HTMLElement {
      constructor() {
        super();
        
        this.coverImage = null;
        this.modalParent = null;
        this.loadIframe = this.loadIframe.bind(this);
        this.handleModalOpen = this.handleModalOpen.bind(this);
        this.handleModalClose = this.handleModalClose.bind(this);
      }
  
      connectedCallback() {
        this.coverImage = this.querySelector("[data-cover-image]");
        this.modalParent = this.closest("pk-modal");
    
        if (this.coverImage) {
          this.coverImage.addEventListener("click", this.loadIframe);
        } else if (this.modalParent) { 
          this.modalParent.addEventListener("open", this.handleModalOpen.bind(this));
          this.modalParent.addEventListener("close", this.handleModalClose.bind(this));
        } else { 
          this.loadIframe();
        }
      }
  
      disconnectedCallback() {
        if (this.coverImage) {
          this.coverImage.removeEventListener("click", this.loadIframe);
        } else if (this.modalParent) {
          this.modalParent.removeEventListener("open", this.handleModalOpen);
          this.modalParent.removeEventListener("close", this.handleModalClose);
        }
      }
  
      handleModalOpen(event) {
        if (!this.hasAttribute("loaded")) {
          this.loadIframe();
        }
      }
  
      handleModalClose() {
        const iframe = this.querySelector("iframe");
        if (iframe) {
          iframe.setAttribute("src", "");
          setTimeout(() => iframe.remove(), 100);
          this.removeAttribute("loaded");
        }
      }
  
      loadIframe() {
        if (!this.getAttribute('loaded')) {
          this.pauseAllMedia();
          
          const content = document.createElement('div');
          content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
  
          this.setAttribute('loaded', true);
  
          const iframe = content.querySelector('iframe');
          if (!iframe) return;
          
          this.appendChild(iframe);
  
          if (this.coverImage) {
            this.coverImage.removeEventListener("click", this.loadIframe);
            this.coverImage.remove();
            this.coverImage = null;
          }
        }
      }
  
      pauseAllMedia() {
        document.querySelectorAll('iframe').forEach((iframe) => {
          const src = iframe.getAttribute("src") || "";
          if (!iframe.contentWindow || !src) return;
      
          if (src.includes("youtube.com/embed/") && !src.includes("controls=0")) {
            iframe.contentWindow.postMessage(
              '{"event":"command","func":"pauseVideo","args":""}', '*'
            );
          }
      
          if (src.includes("player.vimeo.com/video/") && !src.includes("background=0")) {
            iframe.contentWindow.postMessage(
              '{"method":"pause"}', '*'
            );
          }
        });
      
        document.querySelectorAll('video').forEach((video) => {
          if (video.hasAttribute("controls") && !video.hasAttribute("muted")) {
            video.pause();
          }
        });
      }
    }
  
    customElements.define('pk-external-video', PkExternalVideo);
  }
});
